import { Box, LinkButton, Typography } from "@bca/ui-library";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";

import LotNotFoundSVG from "../Icons/LotNotFoundSVG";
import getEnvironment from "../getEnvironment";
import noResultsDataPush from "../AdobeDataPushes/errors/noResultsDataPush";
import NoResultsMessage from "./NoResultsMessage";
import { PurchasesUrlQueryParams } from "../APICalls/buildGetPurchasesUrl";
import { AppliedCheckboxResult } from "../Hooks/useAppliedCheckBoxes";

const Container = styled(Box)`
  ${css({
    border: "normalBorder",
    color: "grey-blue",
  })}
`;

const CTA_REQUIRED_CASE = "1 Year";

interface EmptyStateProps {
  selectedFilterLabel: string;
  purchasesUrlQueryParams: PurchasesUrlQueryParams;
}

const NoResults: React.FunctionComponent<
  EmptyStateProps & AppliedCheckboxResult
> = ({ selectedFilterLabel, purchasesUrlQueryParams, appliedCheckboxes }) => {
  const environment = getEnvironment();
  const isProd = environment === "production";
  const searchLink = isProd
    ? "https://auctionview.bca.co.uk/Buyer/FacetedSearch"
    : "https://auctionview.uat.britishcarauctions.net/Buyer/FacetedSearch";
  const salesLink = isProd
    ? "https://www.bca.co.uk/sales"
    : "https://cms.uat.britishcarauctions.net/sales";
  const shouldShowCTAs = selectedFilterLabel === CTA_REQUIRED_CASE;
  useEffect(() => {
    noResultsDataPush(selectedFilterLabel);
  }, [
    selectedFilterLabel,
    purchasesUrlQueryParams.q,
    purchasesUrlQueryParams.sort,
  ]);

  const getSubMessage = () => {
    if (shouldShowCTAs) {
      return `We’ve been unable to return any results for your search. Please try again, alternatively, you can search or browse our sales.`;
    }
    return `We’ve been unable to return any results. Please extend your date range.`;
  };
  return (
    <Container
      width="100%"
      display="flex"
      justifyContent="center"
      alignItems={{
        mobile: "center",
        tablet: "center",
        desktop: "flex-start",
      }}
      flexDirection="column"
      mr={5}
    >
      <LotNotFoundSVG width="64px" height="64px" mb={20}></LotNotFoundSVG>
      <NoResultsMessage
        selectedFilterLabel={selectedFilterLabel}
        purchasesUrlQueryParams={purchasesUrlQueryParams}
        appliedCheckboxes={appliedCheckboxes}
      />
      <Typography
        variant="body"
        paddingBottom={0}
        marginBottom={0}
        textAlign={{
          mobile: "center",
          tablet: "center",
          desktop: "left",
        }}
      >
        {getSubMessage()}
      </Typography>
      {shouldShowCTAs && (
        <Box
          display="flex"
          flexDirection={{
            mobile: "column",
            desktop: "row",
          }}
          paddingTop={{
            mobile: "31px",
            desktop: "33px",
          }}
          width="100%"
        >
          <LinkButton
            variant="primary"
            shouldOpenInNewWindow={false}
            fontSize={18}
            margin={0}
            mb={6}
            padding={0}
            link={searchLink}
            fontWeight={100}
            marginRight={{
              mobile: "0px",
              desktop: 5,
            }}
            data-en="click"
            data-ec="button"
            data-el="search-for-vehicles"
            data-ea="navigate"
            data-es=""
          >
            Search for vehicles
          </LinkButton>
          <LinkButton
            variant="secondary"
            shouldOpenInNewWindow={false}
            fontSize={18}
            margin={0}
            mb={6}
            padding={0}
            link={salesLink}
            fontWeight={100}
            marginRight={{
              mobile: "0px",
              desktop: 5,
            }}
            data-en="click"
            data-ec="button"
            data-el="browse-all-sales"
            data-ea="navigate"
            data-es=""
          >
            Browse All Sales
          </LinkButton>
        </Box>
      )}
    </Container>
  );
};

export default NoResults;
